import { CodedProperty, NamedProperty } from "sprint-common-web";

import { OrderRequestDetailsResponseItem } from "../../../common/api/orders-api/order-request/GetOrderRequestDetailsResponse";
import {
    resolveOrderContentTitle,
    resolveOrderContentVersion
} from "../../../common/api/orders-api/order/ContentReference";
import { NoteItem } from "../../../common/components/notes/hooks/state/NoteItem";
import { DynamicSubtitleType } from "../../../common/types/DynamicSubtitleType";
import { OrderContentReference } from "../../../common/types/OrderContentReference";
import { OrderRequestSource } from "../../../common/types/OrderRequestSource";
import { OrderRequestStatus } from "../../../common/types/OrderRequestStatus";
import { OrderAssetAvailability } from "../../common/types/OrderAssetAvailability";
import { OrderResolutionDimensions } from "../../common/types/OrderResolutionDimensions";

export interface OrderRequestDashboardItem {
    id: string;
    pONumber: string;
    airline?: CodedProperty;
    content?: OrderContentReference;
    contentTitle?: string;
    systemPlatform?: OrderRequestSystemPlatform;
    cloudAssets: OrderRequestAssets;
    onPremAssets: OrderRequestAssets;
    status: OrderRequestStatus;
    startDate: string;
    dueDate: string;
    buzzId?: string;
    shreqId?: string;
    modifiedBy?: string;
    statusDetails?: string;
    isTrailer?: boolean;
    runtime?: string;
    deliveryDetails?: string;
    deliveryTo?: string;
    materialSpecification: OrderRequestMaterialSpecification;
    encodeSpecification?: EncodeSpecification;
    targetResolution?: OrderResolutionDimensions;
    notes?: string;
    isRevised?: boolean;
    assetNotes: NoteItem[];
    source: OrderRequestSource;
}

interface OrderRequestSystemPlatform {
    system: NamedProperty;
}

interface OrderRequestMaterialSpecification {
    encodingFormat?: string;
    aspectRatio?: string;
    bitRate?: string;
    resolution?: string;
    runtime?: string;
}

interface OrderRequestAssets {
    video: OrderRequestVideo;
    soundtracks: OrderRequestSoundtrack[];
    subtitles: OrderRequestSubtitle[];
}

interface OrderRequestAssetBase {
    assetId?: string;
    filePath?: string;
    fileType?: number;
}

export interface OrderRequestVideo extends OrderRequestAssetBase {
    targetFilename: string;
    availability: OrderAssetAvailability;
    aspectRatio: NamedProperty;
    sourceResolution?: OrderResolutionDimensions;
    targetResolution?: OrderResolutionDimensions;
    subtitle: CodedProperty;
    nonMatchingVideos: OrderRequestNonMatchingVideo[];
}

interface OrderRequestNonMatchingVideo {
    assetId: string;
    subtitle: CodedProperty;
    aspectRatio: NamedProperty;
    resolution: OrderResolutionDimensions;
}

export interface OrderRequestSoundtrack extends OrderRequestAssetBase {
    language: CodedProperty;
    availability: OrderAssetAvailability;
}

export interface OrderRequestSubtitle extends OrderRequestAssetBase {
    language: CodedProperty;
    availability: OrderAssetAvailability;
    subtitleType: DynamicSubtitleType | undefined;
    targetFilename?: string;
}

interface EncodeSpecification {
    id: string;
    name: string;
    targetAspectRatio: NamedProperty;
}

export const mapToOrderRequestDashboardItem = (
    response: OrderRequestDetailsResponseItem
): OrderRequestDashboardItem => ({
    id: response.id,
    pONumber: response.pONumber,
    airline: response.airline,
    content: response.content,
    contentTitle: response.content
        ? `${resolveOrderContentTitle(response.content)} - ${resolveOrderContentVersion(response.content)}`
        : undefined,
    systemPlatform: response.systemPlatform,
    targetResolution: response.targetResolution,
    cloudAssets: response.cloudAssets,
    onPremAssets: response.onPremAssets,
    status: response.status,
    startDate: response.startDate,
    dueDate: response.dueDate,
    buzzId: response.buzzId,
    modifiedBy: response.audit.updatedBy,
    statusDetails: response.statusDetails,
    isTrailer: response.isTrailer,
    runtime: response.runtime,
    deliveryDetails: response.deliveryDetails,
    deliveryTo: response.deliveryTo,
    materialSpecification: response.materialSpecification,
    encodeSpecification: response.encodeSpecification,
    notes: response.notes,
    isRevised: response.isRevised,
    assetNotes: [],
    source: response.source
});
